/**
 * The paywall notice (D165, platform.md §4): the buttons that are the
 * platform's at the edge of the demo — Buy, and (D165c) the pass beside it
 * when the pass covers the game.
 *
 * @remarks
 * The game's own words for the wall are the turn's answer, and the shell
 * draws them in the transcript, under the words the player typed. They
 * stood here once, in a panel outside the scroll region, and at a desktop
 * height four lines and two buttons took all of the room the transcript
 * had (Mike, 2026-11-02). What is left is the shell's voice: the buttons,
 * and the one chrome line under them when a checkout could not start.
 * Pure: every word is a prop. `role="status"`, so the chrome's line is
 * heard when it lands.
 *
 * @packageDocumentation
 */

/** Props for {@link PaywallNoticeComponent}. */
export interface PaywallNoticeComponentProps {
	readonly buyLabel: string;
	/** Whether a checkout is on its way; both buttons wait. */
	readonly isStartingCheckout: boolean;
	/** The chrome's one line, when a checkout could not start. */
	readonly notice?: string;
	readonly onBuy: () => void;
	/** Absent, the pass is not offered here or does not cover this game: Buy stands alone. */
	readonly onPass?: () => void;
	/** The pass button's label — present exactly when `onPass` is. */
	readonly passLabel?: string;
	/**
	 * One line of chrome under the two buttons saying what the pass covers (D166, Calder's finding): beside "Buy the game —
	 * $14.99", "Get the pass — $14.99 a month" reads as strictly worse unless something on screen says it is more than this
	 * game. Shown only with the pass button.
	 */
	readonly passNote?: string;
}

/**
 * Render the notice.
 *
 * @param props - {@link PaywallNoticeComponentProps}
 * @returns The notice
 */
export const PaywallNoticeComponent = ({ buyLabel, isStartingCheckout, notice, onBuy, onPass, passLabel, passNote }: PaywallNoticeComponentProps) => (
	<div className="paywall-notice" role="status">
		<button className="paywall-notice__buy" disabled={isStartingCheckout} onClick={onBuy} type="button">
			{buyLabel}
		</button>
		{onPass === undefined || passLabel === undefined ? undefined : (
			<button className="paywall-notice__pass" disabled={isStartingCheckout} onClick={onPass} type="button">
				{passLabel}
			</button>
		)}
		{onPass === undefined || passNote === undefined ? undefined : <p className="paywall-notice__pass-note">{passNote}</p>}
		{notice === undefined ? undefined : <p className="paywall-notice__chrome">{notice}</p>}
	</div>
);
